import React, { useEffect } from 'react';
import { Radio, Input } from 'antd';
import useFetchReducer from 'src/hooks/useFetchReducer';
import ScriptActivationIcon, { IconState } from '../../ScriptActivationIcon';
import ScriptActivationDropdown from './ScriptActivationDropdown';
import { AllStatusReasons, getAllScriptStatusReasons } from '../../services/qaErrors.service';
import styles from './ScriptActivationModal.module.less';

const { TextArea } = Input;

export const defaultStatusId = -1;

export enum ScriptAction {
  ActivateScript = 'activation',
  DeactivateScript = 'deactivation',
  SendCustomMessage = 'maintenance_msg',
}

const actionLabels = {
  [ScriptAction.ActivateScript]: {
    active: 'Keep it enabled and change the reason to',
    inactive: 'Enable this script',
  },
  [ScriptAction.DeactivateScript]: {
    active: 'Deactivate the script because',
    inactive: 'Keep the script deactivated and change the reason to',
  },
  [ScriptAction.SendCustomMessage]: {
    active: 'Keep the script enabled and send a custom message to all subscribers',
    inactive: 'Keep the script deactivated and send a custom message to all subscribers',
  },
} satisfies Record<ScriptAction, Record<'active' | 'inactive', string>>;

const getActionLabel =
  (isActive: boolean) =>
  (action: ScriptAction): string => {
    return actionLabels[action][isActive ? 'active' : 'inactive'];
  };

export type SelectedAction = {
  action: ScriptAction;
  enableReason: number;
  disableReason: number;
  customMessage?: string;
};

type ScriptActionsProps = {
  value: SelectedAction;
  isActive: boolean;
  statusId?: number;
  setValue: (value: SelectedAction) => void;
  setCustomMessage: (value: string) => void;
};

const ScriptActivationActions: React.FC<ScriptActionsProps> = ({
  value,
  isActive,
  statusId = defaultStatusId,
  setValue,
  setCustomMessage,
}) => {
  const { action: selectedAction, enableReason, disableReason, customMessage } = value;
  const [{ data: statusReasons }] = useFetchReducer<AllStatusReasons>(getAllScriptStatusReasons, []);

  const renderLabel = getActionLabel(isActive);
  const updateValue = (ob: Partial<SelectedAction>) => setValue({ ...value, ...ob });

  useEffect(() => {
    if (statusReasons) {
      const { activateReasons, deactivateReasons } = statusReasons;
      const defaultEnableReason = activateReasons[0].value;
      const defaultDisableReason = deactivateReasons[0].value;

      if (statusId === defaultStatusId) {
        updateValue({
          enableReason: defaultEnableReason,
          disableReason: defaultDisableReason,
        });
      } else if (activateReasons.find(({ value }) => value === statusId)) {
        updateValue({
          disableReason: defaultDisableReason,
        });
      } else if (deactivateReasons.find(({ value }) => value === statusId)) {
        updateValue({
          enableReason: defaultEnableReason,
        });
      } else {
        updateValue({});
      }
    }
  }, [statusReasons]);

  return (
    <Radio.Group value={selectedAction} onChange={(event) => updateValue({ action: event.target.value })}>
      <Radio value={ScriptAction.ActivateScript}>
        <ScriptActivationIcon value={IconState.Active} className={styles.icon} />
        {renderLabel(ScriptAction.ActivateScript)}:
      </Radio>
      <ScriptActivationDropdown
        value={enableReason}
        options={statusReasons?.activateReasons ?? []}
        className={styles.select}
        disabled={selectedAction !== ScriptAction.ActivateScript}
        onChange={(reason) => updateValue({ enableReason: reason })}
      />

      <Radio value={ScriptAction.DeactivateScript}>
        <ScriptActivationIcon value={IconState.Inactive} className={styles.icon} />
        {renderLabel(ScriptAction.DeactivateScript)}:
      </Radio>
      <ScriptActivationDropdown
        value={disableReason}
        options={statusReasons?.deactivateReasons ?? []}
        className={styles.select}
        disabled={selectedAction !== ScriptAction.DeactivateScript}
        onChange={(reason) => updateValue({ disableReason: reason })}
      />

      <Radio value={ScriptAction.SendCustomMessage}>
        <ScriptActivationIcon value={IconState.Warning} className={styles.icon} />
        {renderLabel(ScriptAction.SendCustomMessage)}:
      </Radio>
      <TextArea
        value={customMessage}
        className={styles.message}
        disabled={selectedAction !== ScriptAction.SendCustomMessage}
        onChange={(event) => {
          updateValue({ customMessage: event.target.value });
          setCustomMessage(event.target.value);
        }}
      />
    </Radio.Group>
  );
};

export default ScriptActivationActions;
